import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "react-query";
import { ChevronLeft, ChevronRight, Search, FileText } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import api from "@/lib/api";
import { toast } from "react-hot-toast";
import { Label } from "@/components/ui/Label";
import RoleSelector from "@/components/auth/RoleSelector";
import GenerateReportDialog from "@/components/reports/GenerateReportDialog";
import {
  getReviewerStartedStatus,
  getReviewerUploadedStatus,
  shouldStartOnDownload,
} from "@/lib/reviewerStackUi";

const DIRECTOR_REVIEWER_API = "director-reviewer";

const DirectorReviewerReports = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [reportStatuses, setReportStatuses] = useState({});
  const [readyForReupload, setReadyForReupload] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [generateDialog, setGenerateDialog] = useState({
    isOpen: false,
    reportId: null,
    reportName: "",
    phase: "confirm",
    intent: "view",
  });

  // Dynamic file types - will be set based on original skeleton file format
  const [allowedFileTypes, setAllowedFileTypes] = useState(['application/msword']);
  const [expectedFileFormat, setExpectedFileFormat] = useState('doc');
  const [expectedFilename, setExpectedFilename] = useState('');

  // Debounce search input
  useEffect(() => {
    if (search !== debouncedSearch) {
      setIsSearching(true);
    }

    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setIsSearching(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);

  const { data } = useQuery(
    ["director-reviewer-reports", page, debouncedSearch, statusFilter],
    async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "10",
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(statusFilter && { status: statusFilter }),
      });
      const response = await api.get(`/director-reviewer?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true,
      staleTime: 0,
      cacheTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  const { data: statusData } = useQuery(
    ["director-report-statuses", data?.data?.map((r) => r.id)],
    async () => {
      if (!data?.data || data.data.length === 0) return {};
      const statusPromises = data.data.map(async (report) => {
        try {
          const response = await api.get(`/director-reviewer/${report.id}/latest-status`);
          return { [report.id]: response.data.status };
        } catch {
          return { [report.id]: "unknown" };
        }
      });
      const results = await Promise.all(statusPromises);
      const statusMap = {};
      results.forEach((r) => Object.assign(statusMap, r));
      return statusMap;
    },
    {
      enabled: !!data?.data && data.data.length > 0,
      staleTime: 0,
      cacheTime: 0,
      keepPreviousData: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
    }
  );

  useEffect(() => {
    if (statusData) setReportStatuses(statusData);
  }, [statusData]);

  // Button visibility helpers based on latest status
  const shouldShowDownload = (reportId) => {
    const status = reportStatuses[reportId];
    const uploadedStatus = getReviewerUploadedStatus("director");
    return (
      (status === "awaiting_director_reviewer" || status === uploadedStatus) &&
      !readyForReupload[reportId]
    );
  };
  const shouldShowUpload = (reportId) =>
    reportStatuses[reportId] === getReviewerStartedStatus("director") || !!readyForReupload[reportId];
  const shouldShowCompleteReject = (reportId) => reportStatuses[reportId] === "uploaded_director_review";
  const hasLoadedStatus = (reportId) => Object.prototype.hasOwnProperty.call(reportStatuses, reportId);

  // Function to get expected file format for a report
  const getExpectedFileFormat = async (reportId) => {
    try {
      const response = await api.get(`/director-reviewer/${reportId}/original-format`);
      const extension = response.data.originalFormat.toLowerCase();
      setExpectedFileFormat(extension);

      // Set allowed MIME types based on extension
      const mimeTypes = {
        'doc': ['application/msword'],
        'docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        'pdf': ['application/pdf']
      };

      setAllowedFileTypes(mimeTypes[extension] || ['application/msword']);
    } catch (error) {
      setExpectedFileFormat('doc');
      setAllowedFileTypes(['application/msword']);
    }
  };

  // Function to get expected filename for a report
  const getExpectedFilename = async (reportId) => {
    try {
      const response = await api.get(`/director-reviewer/${reportId}/expected-filename`);
      setExpectedFilename(response.data.expectedFilename);
    } catch (error) {
      setExpectedFilename('');
    }
  };

  const openUploadModal = async (reportId) => {
    setSelectedReportId(reportId);
    setIsModalOpen(true);
    setUploadedFile(null);

    // Get expected file format and filename for this report
    await Promise.all([
      getExpectedFileFormat(reportId),
      getExpectedFilename(reportId)
    ]);
  };

  const handleFiles = (files) => {
    if (uploadedFile) {
      toast.error("A file is already selected. Remove it to upload a different file.");
      return;
    }

    const maxFileSize = 25 * 1024 * 1024;
    const validFiles = files.filter((f) => {
      // Check file type
      if (!allowedFileTypes.includes(f.type)) {
        const formatText = expectedFileFormat ? `${expectedFileFormat.toUpperCase()} files` : 'DOC files';
        toast.error(`Only ${formatText} are allowed for this report.`);
        return false;
      }

      // Check file size
      if (f.size > maxFileSize) {
        toast.error(`File too large. Maximum size is 25MB.`);
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;
    setUploadedFile(validFiles[0]);
    toast.success("File uploaded successfully!");
  };

  const handleViewUploadedFile = (file) => {
    if (!file) return toast.error("No file to view");
    const url = URL.createObjectURL(file);
    const w = window.open(url, "_blank");
    if (!w) return toast.error("Popup blocked. Please allow popups for this site.");
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setUploadedFile(null);
    setSelectedReportId(null);
    setIsDragging(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!uploadedFile) return toast.error("Please upload the file. If it’s already uploaded, replace it and upload again.");
    if (!selectedReportId) return toast.error("No report found");
    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append("report_file", uploadedFile);
      const response = await api.post(
        `/director-reviewer/${selectedReportId}/updated-report`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      if (response.data.success) {
        toast.success("Report updated successfully");
        setReadyForReupload((prev) => ({ ...prev, [selectedReportId]: false }));
        closeModal();
        queryClient.invalidateQueries(["director-reviewer-reports"]);
        queryClient.invalidateQueries(["director-report-statuses"]);
      } else {
        toast.error("Failed to update report");
      }
    } catch (err) {
      // Handle specific validation errors
      if (err.response?.data?.code === 'DUPLICATE_CONTENT') {
        toast.error(err.response.data.message || "Please upload an updated file with different content");
      } else if (err.response?.data?.code === 'FORMAT_MISMATCH') {
        toast.error(err.response.data.message || "File format does not match the expected format");
      } else {
        toast.error(err.response?.data?.message || "Error uploading file");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const resetGenerateDialog = () =>
    setGenerateDialog({
      isOpen: false,
      reportId: null,
      reportName: "",
      phase: "confirm",
      intent: "view",
    });

  const openGenerateDialog = (reportId, reportName, intent) => {
    setGenerateDialog({
      isOpen: true,
      reportId,
      reportName: reportName || "",
      phase: "confirm",
      intent,
    });
  };

  const handleGenerateDialogClose = () => {
    if (generateDialog.phase === "processing") return;
    resetGenerateDialog();
  };

  const handleGenerateConfirm = async () => {
    const { reportId, intent } = generateDialog;
    if (!reportId || !intent) return;

    setGenerateDialog((prev) => ({ ...prev, phase: "processing" }));

    try {
      if (intent === "view") {
        const response = await api.post(
          `/${DIRECTOR_REVIEWER_API}/${reportId}/generate-with-values?action=view`,
          {},
          { timeout: 300000 }
        );

        const fileUrl = response.data.url;
        if (!fileUrl) {
          toast.error("No file URL returned", { id: "view-file" });
          return;
        }

        const w = window.open(fileUrl, "_blank");
        if (!w) {
          toast.error("Popup blocked. Please allow popups for this site.", { id: "view-file" });
          return;
        }

        toast.success("File opened successfully", { id: "view-file" });
      } else if (intent === "download") {
        const response = await api.post(
          `/${DIRECTOR_REVIEWER_API}/${reportId}/generate-with-values?action=download`,
          {},
          { timeout: 300000 }
        );

        const { url, filename } = response.data;
        if (!url) throw new Error("No file URL returned");

        const link = document.createElement("a");
        link.href = url;
        link.download = filename || `report-${reportId}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success("File downloaded successfully", { id: "download-file" });

        const currentStatus = reportStatuses[reportId];
        if (shouldStartOnDownload(currentStatus, "director")) {
          refreshStatuses({
            optimistic: true,
            reportId,
            status: getReviewerStartedStatus("director"),
          });
        } else if (currentStatus === getReviewerUploadedStatus("director")) {
          setReadyForReupload((prev) => ({ ...prev, [reportId]: true }));
        }
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        (intent === "view" ? "Failed to load file" : "Failed to download file"),
        { id: intent === "view" ? "view-file" : "download-file" }
      );
    } finally {
      resetGenerateDialog();
    }
  };

  const refreshStatuses = (optimistic) => {
    if (optimistic && optimistic.reportId && optimistic.status) {
      setReportStatuses((prev) => ({ ...prev, [optimistic.reportId]: optimistic.status }));
    }
    queryClient.invalidateQueries(["director-report-statuses"]);
  };

  const handleComplete = async (reportId) => {
    try {
      const latestUserFile = await api.get(`/director-reviewer/${reportId}/latest-user-file`);
      if (!latestUserFile.data?.hasFile) {
        toast.error("Please upload the updated report before completing.");
        return;
      }
      const response = await api.post(`/director-reviewer/${reportId}/complete`);
      if (response.data.success) {
        toast.success("Report completed successfully!", { id: "complete-report" });
        queryClient.invalidateQueries(["director-reviewer-reports"]);
        queryClient.invalidateQueries(["director-report-statuses"]);
      } else {
        toast.error("Failed to complete report", { id: "complete-report" });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to complete report", {
        id: "complete-report",
      });
    }
  };

  const handleReject = async (reportId) => {
    try {
      const response = await api.post(`/director-reviewer/${reportId}/reject`);
      if (response.data.success) {
        toast.success("Report rejected and reassigned to Field Engineer", { id: "reject-report" });
        queryClient.invalidateQueries(["director-reviewer-reports"]);
        queryClient.invalidateQueries(["director-report-statuses"]);
      } else {
        toast.error("Failed to reject report", { id: "reject-report" });
      }
    } catch (error) {
      // Handle specific rejection validation errors
      if (error.response?.data?.code === 'NO_FILE_UPLOADED') {
        toast.error(error.response.data.message || "You must upload a file before rejecting");
      } else if (error.response?.data?.code === 'NO_CHANGES_DETECTED') {
        toast.error(error.response.data.message || "You must modify the file before rejecting");
      } else if (error.response?.data?.code === 'NO_COMPARISON_FILE_FOUND') {
        toast.error(error.response.data.message || "System error: No comparison file found");
      } else {
        toast.error(error.response?.data?.message || "Failed to reject report");
      }
    }
  };

  // Allowable statuses:
  const DRAllowedStatuses = [
    "awaiting_director_reviewer",
    "started_director_review",
    "uploaded_director_review",
  ];

  const filteredReports = data?.data?.filter((report) => {
    const status = reportStatuses[report.id];
    return DRAllowedStatuses.includes(status);
  }) || [];

  const ReportColumn = () => {
    // Show spinner when searching
    if (isSearching) {
      return (
        <div className="w-full py-12 text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-500">Searching reports...</p>
        </div>
      );
    }

    if (!filteredReports.length) {
      return (
        <div className="w-full py-12 text-center">
          <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400 opacity-50" />
          <p className="text-gray-500">No reports assigned to you</p>
        </div>
      );
    }

    return (
      <div className="w-full overflow-x-auto">
        {filteredReports.map((report) => (
          <div key={report.id} className="mb-3 border-gray-600">
            <div className="flex gap-3 flex-nowrap">
              <div>
                <div className="p-4 rounded-t-md bg-navy-100 w-80">
                  <div className="text-xs font-medium truncate text-gray-0 whitespace-nowrap">{report.report_name}</div>
                </div>
              </div>
              <div>
                <div className="p-4 rounded-t-md bg-navy-100 w-80">
                  <div className="text-xs font-medium truncate text-gray-0 whitespace-nowrap">
                    {reportStatuses[report.id]
                      ? reportStatuses[report.id]
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase())
                      : "Unknown"}
                  </div>
                </div>
              </div>
              <div className="!ml-1 w-full xl:w-2/3">
                <div className="flex items-center flex-nowrap gap-y-4 lg:flex-nowrap">
                  <div className="px-2">
                    <Button
                      onClick={() => openGenerateDialog(report.id, report.report_name, "view")}
                      disabled={generateDialog.phase === "processing"}
                      className="hover:text-red-600 h-auto w-36 rounded-b-none rounded-t-md border-0 bg-navy-100 px-0.5 py-4 text-xs font-normal uppercase text-gray-0 hover:bg-navy-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      View
                    </Button>
                  </div>
                  {hasLoadedStatus(report.id) && shouldShowDownload(report.id) && (
                    <div className="px-2">
                      <Button
                        onClick={() => openGenerateDialog(report.id, report.report_name, "download")}
                        disabled={generateDialog.phase === "processing"}
                        className="hover:text-red-600 h-auto w-36 rounded-b-none rounded-t-md border-0 bg-navy-100 px-0.5 py-4 text-xs font-normal uppercase text-gray-0 hover:bg-navy-100 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Download
                      </Button>
                    </div>
                  )}
                  {hasLoadedStatus(report.id) && shouldShowUpload(report.id) && (
                    <div className="px-2">
                      <Button
                        className="hover:text-red-600 h-auto w-36 rounded-b-none rounded-t-md border-0 bg-navy-100 px-0.5 py-4 text-xs font-normal uppercase text-gray-0 hover:bg-navy-100 disabled:opacity-50"
                        onClick={() => openUploadModal(report.id)}
                      >
                        Upload
                      </Button>
                    </div>
                  )}
                  {hasLoadedStatus(report.id) && shouldShowCompleteReject(report.id) && (
                    <div className="px-2">
                      <Button
                        className="hover:text-red-600 h-auto w-36 rounded-b-none rounded-t-md border-0 bg-navy-100 px-0.5 py-4 text-xs font-normal uppercase text-gray-0 hover:bg-navy-100 disabled:opacity-50"
                        onClick={() => handleComplete(report.id)}
                      >
                        Complete
                      </Button>
                    </div>
                  )}
                  {hasLoadedStatus(report.id) && shouldShowCompleteReject(report.id) && (
                    <div className="px-2">
                      <Button
                        className="hover:text-red-600 h-auto w-36 rounded-b-none rounded-t-md border-0 bg-navy-100 px-0.5 py-4 text-xs font-normal uppercase text-gray-0 hover:bg-navy-100 disabled:opacity-50"
                        onClick={() => handleReject(report.id)}
                      >
                        Reject
                      </Button>
                    </div>
                  )}
                  {/* Status tooltip removed; actions visibility controlled by latest status */}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="slide-up">
        <div className="border-b border-gray-600 slide-up">
          <div className="flex flex-col items-center justify-between w-full gap-4 pb-5 md:flex-row md:items-start">
            <div className="w-full md:w-1/3">
              <RoleSelector />
            </div>
            <div className="flex justify-end w-full md:w-1/2">
              <form className="w-full max-w-96" onSubmit={(e) => e.preventDefault()}>
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Search reports..."
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full h-auto py-4 pr-12 text-xs border-0 rounded-b-none rounded-t-md bg-navy-100 text-gray-0 placeholder:font-medium placeholder:uppercase placeholder:text-gray-600"
                  />
                  <Search className="absolute w-4 h-4 transform -translate-y-1/2 right-4 top-1/2 text-gray-0" />
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="py-2">
          <div className="flex gap-8">
            <ReportColumn />
          </div>

          {data?.pagination && (
            <div className="flex flex-col gap-3 pr-0 border-t border-b border-gray-0 sm:flex-row sm:justify-between sm:pr-12">
              <div className="py-4 text-sm text-center text-gray-400 uppercase sm:text-left">
                Showing {(data.pagination.page - 1) * data.pagination.limit + 1} to{" "}
                {Math.min(data.pagination.page * data.pagination.limit, data.pagination.total)} of{" "}
                {data.pagination.total} reports
              </div>
              <div className="flex items-center justify-center w-full sm:w-auto sm:justify-center">
                <Button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="flex items-center justify-center h-auto p-5 text-xs font-normal uppercase bg-transparent border-r rounded-none rounded-b-none border-gray-0 text-gray-0 hover:scale-100 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4 mr-2 text-red-500" />
                  Previous
                </Button>
                <Button className="flex items-center justify-center h-auto p-5 text-xs font-normal uppercase bg-transparent border-r rounded-none rounded-b-none border-gray-0 text-gray-0 hover:scale-100">
                  {page}
                </Button>
                <Button
                  onClick={() => setPage(Math.min(data.pagination.pages, page + 1))}
                  disabled={page === data.pagination.pages}
                  className="flex items-center justify-center h-auto p-5 text-xs font-normal uppercase bg-transparent border-0 rounded-none rounded-b-none text-gray-0 hover:scale-100 disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-2 text-red-500" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 transition-opacity bg-white bg-opacity-50 backdrop-blur-sm" />
          <div className="relative z-50 w-full max-w-2xl p-6 overflow-hidden transition-all duration-300 transform shadow-2xl animate-in fade-in-0 zoom-in-95 rounded-2xl bg-navy-500">
            <form onSubmit={handleSubmit}>
              <div className="space-y-3">
                <Label
                  htmlFor="reportStatus"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Upload Updated report *
                </Label>

                {!uploadedFile && (
                  <div
                    className={`rounded-lg border-2 border-dashed bg-navy-100 p-6 text-center transition-all duration-300 ${isDragging ? "border-red-500 shadow-lg" : "border-gray-600"}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsDragging(false);
                      if (uploadedFile) {
                        toast.error(
                          "A file is already selected. Please remove it first to upload a different file."
                        );
                        return;
                      }
                      const files = Array.from(e.dataTransfer.files);
                      if (files.length > 0) {
                        handleFiles(files);
                      }
                    }}
                  >
                    <div className="text-gray-0">
                      <p className="uppercase">
                        {isDragging ? "Drop files here" : "Drag and drop files here"}
                      </p>
                      <p className="mt-2 text-sm">or</p>
                      <input
                        type="file"
                        className="hidden"
                        id="fileInput"
                        accept={expectedFileFormat === 'pdf' ? '.pdf' : expectedFileFormat === 'docx' ? '.docx' : '.doc'}
                        onChange={(e) => {
                          if (uploadedFile) {
                            toast.error(
                              "A file is already selected. Please remove it first to upload a different file."
                            );
                            return;
                          }
                          const files = Array.from(e.target.files);
                          if (files.length > 0) {
                            handleFiles(files);
                          }
                        }}
                      />
                      <Button
                        type="button"
                        onClick={() => document.getElementById("fileInput").click()}
                        variant="outline"
                      >
                        Browse Files
                      </Button>
                      <p className="mt-2 text-xs leading-snug text-gray-400">
                        Maximum file size: 25MB. Expected format: {expectedFileFormat ? expectedFileFormat.toUpperCase() : 'DOC'} files
                      </p>
                    </div>
                  </div>
                )}

                {uploadedFile && (
                  <div className="text-sm text-gray-100">
                    A new file is selected. You can view it below or remove it to upload a different file.
                  </div>
                )}

                {uploadedFile && (
                  <div className="p-2 border border-gray-600 rounded-lg bg-navy-100">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex flex-col">
                        <span className="text-gray-0">New File</span>
                        <span className="text-xs text-gray-400 break-all">{uploadedFile.name}</span>
                      </div>
                      <div className="flex flex-col w-full gap-2 sm:w-auto sm:flex-row">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => handleViewUploadedFile(uploadedFile)}
                          className="w-full sm:w-auto"
                        >
                          View
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setUploadedFile(null);
                            toast.success("New file removed.");
                          }}
                          className="w-full sm:w-auto"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-4">
                <Button variant="outline" className="flex-1 rounded-xl" onClick={closeModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSaving || !uploadedFile}
                  className="flex-1 bg-green-700 rounded-xl hover:bg-green-800 disabled:opacity-50"
                >
                  {isSaving ? "Reading your file..." : "Save"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <GenerateReportDialog
        isOpen={generateDialog.isOpen}
        phase={generateDialog.phase}
        reportName={generateDialog.reportName}
        intent={generateDialog.intent}
        onClose={handleGenerateDialogClose}
        onConfirm={handleGenerateConfirm}
      />
    </>
  );
};

export default DirectorReviewerReports;
